import React, {useContext} from 'react';
import { Button, ThemeContext } from 'grommet';
import { useRouter } from 'next/router';
import ConnectionCard from "./ConnectionCard";
import useSWR from "swr";
import {handleErrors} from "../../../utils/errors";
import {NotificationsContext} from "../../NotificationsProvider";
import pluralize from 'pluralize';
import {formatDate, formatTime} from "../../../utils/dates";
import withPlanGating from "../../hoc/withPlanGating";

const ApiConnectionComponent = ({ planGating }) => {
    const notificationCtx = useContext(NotificationsContext);
    const theme = useContext(ThemeContext);
    const router = useRouter();

    const fetcher = (url) =>
        fetch(url).then(handleErrors).catch(notificationCtx.showNotification);

    const { data: connectionsData = [] } = useSWR("/api/settings/api-connection", fetcher, {
        revalidateOnFocus: false,
        onError: notificationCtx.showNotification
    });

    const mostRecent = connectionsData.length
        ? new Date(Math.max(...connectionsData.map(c => new Date(c.updated_at))))
        : null;

    const manageKeysButton = () => {
        const originalButton = (
            <Button
                margin={{vertical: 'xxsmall'}}
                label="Manage API Keys"
                secondary
                onClick={()=> router.push('/settings/connections/api')}
                event={{ section: 'api_connection_component', element: 'manage_api_keys' }}
            />
        );

        // If plan gating is active and user doesn't have required plan, render disabled button
        if (planGating && !planGating.hasRequiredPlan) {
            return planGating.buttonRenderer(originalButton);
        }

        return originalButton;
    }

    const getDetailsText = () => {
        // If plan gating is active and user doesn't have required plan, show teaser content
        if (planGating && !planGating.hasRequiredPlan) {
            return planGating.teaserContent;
        }

        return [
            connectionsData.length > 0 ? `You have ${pluralize('active key', connectionsData.length, true)} managing data sync with Streamlined.` : 'Generate a key to push or pull data from Streamlined programmatically.',
            mostRecent ? `Last used ${formatDate(mostRecent)} at ${formatTime(mostRecent)}` : null
        ].filter(Boolean);
    }

    return (
        <ConnectionCard
            logoSrc={`/logo/${theme.dark ? 'white' : 'dark'}-symbol.svg`}
            connectionName="Streamlined API"
            isLoaded={true}
            nested
            menuItems={null}
            details={getDetailsText()}
            cta={manageKeysButton()} />
    );
};

// Wrap the component with plan gating HOC
const PlanGatedApiConnectionComponent = withPlanGating(ApiConnectionComponent, {
    requiredPlans: ['scale', 'enterprise'],
    featureName: 'API connection functionality',
    dataSource: 'session'
});

export default PlanGatedApiConnectionComponent;
